import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, Volume2, VolumeX, Check, ChefHat, Heart, PlayCircle, PauseCircle, Camera } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api, useAuth } from "@/lib/auth";
import { audioUrl } from "@/lib/staticApi";
import { toast } from "sonner";
import PhotoUpload from "@/components/PhotoUpload";
import AvatarOverlay from "@/components/AvatarOverlay";

function fallbackSpeak(text, onEnd) {
  if (!("speechSynthesis" in window)) { onEnd?.(); return null; }
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "es-CL";
  u.rate = 0.95;
  u.pitch = 1.0;
  const voices = window.speechSynthesis.getVoices();
  const esVoice = voices.find((v) => /es-CL|es-MX|es-ES|es-US|spanish/i.test(v.lang || v.name));
  if (esVoice) u.voice = esVoice;
  u.onend = () => onEnd?.();
  window.speechSynthesis.speak(u);
  return u;
}

// Comprueba con HEAD si la URL apunta a un audio real (audio/*) y no al
// fallback SPA (index.html) del hosting. Devuelve `true` si es audio válido.
// Timeout defensivo de 3s para no dejar la UI colgada en redes lentas.
async function isPlayableAudio(url) {
  try {
    const ctl = new AbortController();
    const timeout = setTimeout(() => ctl.abort(), 3000);
    const res = await fetch(url, { method: "HEAD", signal: ctl.signal });
    clearTimeout(timeout);
    if (!res.ok) return false;
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    return ct.startsWith("audio/");
  } catch { return false; }
}

export default function Cook() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [step, setStep] = useState(0); // 0 = ingredients screen, 1..N = steps, N+1 = finished
  const [checked, setChecked] = useState({});
  const [speaking, setSpeaking] = useState(false);
  const [capsule, setCapsule] = useState(null);
  const [showCapsule, setShowCapsule] = useState(false);
  const [capsuleVideo, setCapsuleVideo] = useState(null);
  const [voiceStatus, setVoiceStatus] = useState({ ready: false, is_pvc: false });
  const [autoRead, setAutoRead] = useState(() => {
    // Persistente en localStorage: si el usuario prefiere auto-lectura, se
    // recuerda para próxima sesión. Default = OFF.
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem("recetario_autoread") === "true";
  });
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const { member, token, displayName } = useAuth();
  const audioRef = useRef(null);

  const toggleAutoRead = () => {
    setAutoRead((prev) => {
      const next = !prev;
      try { localStorage.setItem("recetario_autoread", String(next)); } catch { /* ignore */ }
      if (!next) {
        // Si se apaga, detener cualquier audio en curso.
        stopAudio();
      }
      return next;
    });
  };

  const NICK_LABEL = {
    tati: "Tati", lucho: "Lucho", tairuchi: "Tairuchi", liuchi: "Liuchi",
    papa: "Papá", mama: "Mamá", chuki: "Liuchi",
  };
  const cook = (member || "").toLowerCase();

  useEffect(() => {
    api().get(`/recipes/${slug}`).then((res) => setR(res.data)).catch(() => { toast.error("Receta no encontrada"); nav("/cocina"); });
    api().get("/tts/status").then((res) => setVoiceStatus(res.data || {})).catch(() => {});
    return () => {
      window.speechSynthesis?.cancel();
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, [slug, nav]);

  const steps = useMemo(() => (r?.instrucciones || []).map((s) => typeof s === "string" ? s : JSON.stringify(s)), [r]);
  const ings = useMemo(() => (r?.ingredientes || []).map((i) => typeof i === "string" ? i : (i?.nombre || JSON.stringify(i))), [r]);
  const total = steps.length;

  // Auto-lectura: cuando cambia el step y autoRead está activo, dispara
  // toggleSpeak con el texto del paso actual. Ojo:
  //  - NO auto-play en la pantalla de ingredientes (step === 0)
  //  - NO auto-play en la pantalla final (step > total)
  //  - NO auto-play si la cápsula está visible (que lea la cápsula)
  //  - Espera un tick pa' que r y steps estén disponibles
  //  - Cancela el audio anterior antes de arrancar el nuevo
  useEffect(() => {
    if (!autoRead) return;
    if (!r || !steps.length) return;
    if (step <= 0 || step > total) return;
    if (showCapsule) return; // deja que el otro useEffect lea la cápsula
    const currentText = steps[step - 1];
    if (!currentText) return;
    const t = setTimeout(() => {
      stopAudio();
      toggleSpeak(currentText);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, autoRead, r, showCapsule]);

  // Auto-lectura de la cápsula cuando aparece. Si existe el video del avatar
  // (`/avatars/videos/capsule_{id}.mp4`) lo mostramos y omitimos el MP3 —
  // el video ya trae la voz. Si no existe, cae al audio tradicional.
  useEffect(() => {
    if (!autoRead || !showCapsule || !capsule) return;
    const t = setTimeout(() => {
      stopAudio();
      const videoPath = `/avatars/videos/capsule_${capsule.id}.mp4`;
      // HEAD check para saber si el video existe. Si sí, disparamos overlay.
      fetch(videoPath, { method: "HEAD" }).then((r2) => {
        if (r2.ok) {
          setCapsuleVideo(videoPath);
        } else {
          toggleSpeakCapsule(capsule);
        }
      }).catch(() => toggleSpeakCapsule(capsule));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCapsule, capsule, autoRead]);

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; audioRef.current = null; }
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  };

  // Trigger capsule before specific steps
  useEffect(() => {
    if (!r) return;
    if (step > 0 && step < total && step % 3 === 0) {
      // Cargar cápsula + pool de fotos en paralelo. La foto se asigna
      // aleatoriamente desde el pool, para que las cápsulas nunca repitan
      // foto en una sesión larga (~45 fotos únicas en el pool).
      Promise.all([
        api().get("/capsules/random"),
        fetch("/data/family_photos_pool.json").then((r2) => r2.ok ? r2.json() : []).catch(() => []),
      ]).then(([res, pool]) => {
        const cap = res.data;
        if (!cap?.text) return;
        const photoPool = Array.isArray(pool) && pool.length ? pool : [];
        const randPhoto = photoPool.length
          ? photoPool[Math.floor(Math.random() * photoPool.length)]
          : null;
        setCapsule({ ...cap, photo: randPhoto });
        setShowCapsule(true);
      }).catch(() => {});
    } else {
      setShowCapsule(false);
    }
  }, [step, r, total]);

  const handleNext = () => { stopAudio(); setStep((s) => Math.min(s + 1, total + 1)); };
  const handlePrev = () => { stopAudio(); setStep((s) => Math.max(s - 1, 0)); };

  const toggleSpeak = (text) => {
    if (speaking) { stopAudio(); return; }
    setSpeaking(true);
    // Prefer cloned voice via backend if available
    if (voiceStatus.ready) {
      const url = audioUrl(slug, "step", step);
      // Pre-check que el URL apunta a audio real (evita cuelgue si Vercel
      // devuelve index.html como fallback SPA).
      isPlayableAudio(url).then((ok) => {
        if (!ok) {
          fallbackSpeak(text, () => setSpeaking(false));
          return;
        }
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => { setSpeaking(false); audioRef.current = null; };
        audio.onerror = () => {
          audioRef.current = null;
          fallbackSpeak(text, () => setSpeaking(false));
        };
        audio.play().catch(() => {
          audioRef.current = null;
          fallbackSpeak(text, () => setSpeaking(false));
        });
      });
    } else {
      fallbackSpeak(text, () => setSpeaking(false));
    }
  };

  const toggleSpeakFarewell = () => {
    if (speaking) { stopAudio(); return; }
    setSpeaking(true);

    // Invitados: no hay MP3 pre-generado. Pedir TTS on-demand al Worker
    // con voz PVC + nombre del invitado (queda con la voz del Pollo).
    if (cook === "invitado") {
      const guestName = displayName || "";
      const recipeName = r?.nombre || "esta receta";
      const text = guestName
        ? `¡Provecho, ${guestName}! Ojalá hayas disfrutado esta ${recipeName}. Qué gusto tenerte cocinando con nosotros — vuelve cuando quieras.`
        : `¡Provecho! Ojalá hayas disfrutado esta ${recipeName}. Qué gusto tenerte cocinando con nosotros — vuelve cuando quieras.`;
      const workerBase = (process.env.REACT_APP_PAPI_WORKER_URL || "https://preguntale-a-papi.guendelman.workers.dev")
        .replace(/\/ask-papi\/?$/, "")
        .replace(/\/+$/, "");
      const ttsUrl = `${workerBase}/guest-tts`;
      fetch(ttsUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      })
        .then((res) => res.ok ? res.json() : Promise.reject(new Error("guest-tts failed")))
        .then((data) => {
          if (!data.audio_url) throw new Error("no audio_url");
          const audio = new Audio(data.audio_url);
          audioRef.current = audio;
          audio.onended = () => { setSpeaking(false); audioRef.current = null; };
          audio.onerror = () => {
            audioRef.current = null;
            fallbackSpeak(farewellText(), () => setSpeaking(false));
          };
          audio.play().catch(() => {
            audioRef.current = null;
            fallbackSpeak(farewellText(), () => setSpeaking(false));
          });
        })
        .catch(() => fallbackSpeak(farewellText(), () => setSpeaking(false)));
      return;
    }

    // Familia: MP3 pre-generado por miembro. Fallback a browser TTS si no existe.
    if (voiceStatus.ready) {
      const url = audioUrl(slug, "farewell", cook);
      isPlayableAudio(url).then((ok) => {
        if (!ok) {
          fallbackSpeak(farewellText(), () => setSpeaking(false));
          return;
        }
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => { setSpeaking(false); audioRef.current = null; };
        audio.onerror = () => {
          audioRef.current = null;
          fallbackSpeak(farewellText(), () => setSpeaking(false));
        };
        audio.play().catch(() => {
          audioRef.current = null;
          fallbackSpeak(farewellText(), () => setSpeaking(false));
        });
      });
    } else {
      fallbackSpeak(farewellText(), () => setSpeaking(false));
    }
  };

  const farewellText = () => {
    if (cook === "invitado") {
      const name = displayName || "";
      const greeting = name ? `¡Provecho, ${name}!` : "¡Provecho!";
      return `${greeting} Ojalá hayas disfrutado esta receta. Qué gusto tenerte cocinando con nosotros — vuelve cuando quieras.`;
    }
    const label = NICK_LABEL[cook];
    const greeting = label ? `Provecho, ${label}.` : "Provecho, hijos.";
    return `${greeting} Espero que te haya quedado rico. Acaban de hacer ${r?.nombre || "esta receta"}.`;
  };

  // Auto-play farewell once when reaching the finished screen
  const farewellPlayedRef = useRef(false);
  useEffect(() => {
    if (!r) return;
    const isFinishedNow = step > total;
    if (isFinishedNow && !farewellPlayedRef.current) {
      farewellPlayedRef.current = true;
      // small delay so UI mounts and animation starts
      const t = setTimeout(() => toggleSpeakFarewell(), 350);
      return () => clearTimeout(t);
    }
    if (!isFinishedNow) farewellPlayedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, r, total]);

  const toggleSpeakCapsule = (cap) => {
    if (speaking) { stopAudio(); return; }
    setSpeaking(true);
    if (voiceStatus.ready && cap?.id != null && cap.id >= 0) {
      const url = audioUrl(cap.id, "capsule");
      isPlayableAudio(url).then((ok) => {
        if (!ok) {
          fallbackSpeak(`${cap.quote}. ${cap.text}`, () => setSpeaking(false));
          return;
        }
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => { setSpeaking(false); audioRef.current = null; };
        audio.onerror = () => {
          audioRef.current = null;
          fallbackSpeak(`${cap.quote}. ${cap.text}`, () => setSpeaking(false));
        };
        audio.play().catch(() => {
          audioRef.current = null;
          fallbackSpeak(`${cap.quote}. ${cap.text}`, () => setSpeaking(false));
        });
      });
    } else {
      fallbackSpeak(`${cap.quote}. ${cap.text}`, () => setSpeaking(false));
    }
  };

  if (!r) return <div className="min-h-screen flex items-center justify-center text-cocoa font-serif italic">Cargando…</div>;

  const photoUrl = r.photo || null;
  const allChecked = ings.length > 0 && ings.every((_, i) => checked[i]);
  const isIngredients = step === 0;
  const isFinished = step > total;
  const currentStep = !isIngredients && !isFinished ? steps[step - 1] : null;

  return (
    <div className="min-h-screen cook-stage iphone-safe-top flex flex-col">
      <AvatarOverlay
        videoUrl={capsuleVideo}
        size="md"
        onEnded={() => setCapsuleVideo(null)}
        onFallback={() => {
          // Video no cargó (404 o autoplay bloqueado) → cae al MP3 tradicional
          setCapsuleVideo(null);
          if (capsule) toggleSpeakCapsule(capsule);
        }}
      />
      {/* Top bar */}
      <header className="px-4 pt-3 pb-2 flex items-center justify-between bg-paper/70 backdrop-blur-md border-b border-line">
        <Link data-testid="cook-exit-btn" to={`/receta/${slug}`} className="p-2 rounded-full bg-cream border border-line">
          <X className="w-5 h-5 text-cocoa" />
        </Link>
        <div className="text-center">
          <p className="eyebrow text-[10px]">Modo Cocina</p>
          <h2 className="font-display italic text-cocoa text-base leading-tight line-clamp-1 max-w-[60vw]">{r.nombre}</h2>
          {r.kosher_label && (
            <span
              data-testid={`cook-kosher-badge-${r.kosher_label.tone}`}
              className={
                "inline-block mt-1 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider " + (
                  r.kosher_label.tone === "carne" ? "bg-red-900/15 text-red-900" :
                  r.kosher_label.tone === "leche" ? "bg-blue-800/15 text-blue-800" :
                  r.kosher_label.tone === "parve" ? "bg-emerald-800/15 text-emerald-800" :
                  "bg-amber-500/15 text-amber-800"
                )
              }
            >
              {r.kosher_label.tone === "carne" ? "🥩 " : r.kosher_label.tone === "leche" ? "🥛 " : r.kosher_label.tone === "parve" ? "🌿 " : "⚠️ "}
              {r.kosher_label.label}
            </span>
          )}
        </div>
        <div className="w-9 h-9 flex items-center justify-center">
          <button
            data-testid="cook-autoread-toggle"
            onClick={toggleAutoRead}
            title={autoRead ? "Lectura automática ACTIVADA — toca pa' apagarla" : "Toca pa' que Papi lea cada paso solo"}
            aria-label="Alternar lectura automática"
            className={`p-1.5 rounded-full transition ${autoRead ? "bg-amber-deep text-cream" : "bg-cream text-cocoa border border-line"}`}
          >
            {autoRead ? <PauseCircle className="w-5 h-5" /> : <PlayCircle className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Progress dots */}
      <div className="px-5 py-3 flex items-center gap-1.5 justify-center bg-paper/70">
        <span className={`h-1.5 rounded-full transition-all ${isIngredients ? "w-6 bg-amber-deep" : "w-1.5 bg-amber-deep"}`} />
        {steps.map((_, i) => (
          <span key={i} className={`h-1.5 rounded-full transition-all ${step === i + 1 ? "w-6 bg-amber-deep" : (step > i + 1 ? "w-1.5 bg-amber-deep" : "w-1.5 bg-line")}`} />
        ))}
        <span className={`h-1.5 rounded-full transition-all ${isFinished ? "w-6 bg-amber-deep" : "w-1.5 bg-line"}`} />
      </div>

      {/* Stage */}
      <main className="flex-1 overflow-y-auto px-5 pb-32">
        <AnimatePresence mode="wait">
          {isIngredients && (
            <motion.div key="ings" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.35 }} className="max-w-2xl mx-auto pt-4">
              <p className="eyebrow mb-2">Antes de empezar</p>
              <h1 className="font-display italic text-cocoa text-4xl sm:text-5xl leading-tight mb-4">Junta estos ingredientes</h1>
              {photoUrl && <img src={photoUrl} alt="" className="w-full h-56 object-cover rounded-md mb-6" />}
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-cocoa-soft font-serif">{Object.values(checked).filter(Boolean).length} de {ings.length} listos</p>
                <button
                  data-testid="check-all-btn"
                  onClick={() => {
                    const allChecked = ings.length > 0 && ings.every((_, i) => checked[i]);
                    const next = {};
                    if (!allChecked) ings.forEach((_, i) => { next[i] = true; });
                    setChecked(next);
                  }}
                  className="text-sm font-serif italic text-amber-deep hover:underline"
                >
                  {ings.length > 0 && ings.every((_, i) => checked[i]) ? "Desmarcar todos" : "Marcar todos"}
                </button>
              </div>
              <ul data-testid="ingredients-checklist" className="space-y-3">
                {ings.map((ing, i) => (
                  <li key={i}>
                    <button data-testid={`ing-check-${i}`} onClick={() => setChecked((c) => ({ ...c, [i]: !c[i] }))} className={`w-full text-left flex items-center gap-3 p-4 rounded-lg border transition ${checked[i] ? "bg-cream border-amber-deep line-through opacity-60" : "bg-paper border-line"}`}>
                      <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition shrink-0 ${checked[i] ? "bg-amber-deep border-amber-deep" : "border-cocoa-soft"}`}>
                        {checked[i] && <Check className="w-4 h-4 text-cream" />}
                      </span>
                      <span className="font-serif text-cocoa text-lg">{ing}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {currentStep && !showCapsule && (
            <motion.div key={`step-${step}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.35 }} className="max-w-2xl mx-auto pt-4">
              <p className="eyebrow mb-3 flex items-center gap-2">
                Paso {step} de {total}
              </p>
              <div className="bg-cream border border-line rounded-lg p-7 shadow-[0_20px_40px_-22px_rgba(58,26,5,0.25)]">
                <div className="flex items-baseline gap-4 mb-5">
                  <span className="font-display italic text-amber-deep text-7xl leading-none">{step}</span>
                  <span className="font-serif italic text-cocoa-soft">/ {total}</span>
                </div>
                <p data-testid="current-step-text" className="font-serif text-cocoa text-2xl leading-relaxed">
                  {currentStep}
                </p>

                <button
                  data-testid="speak-step-btn"
                  onClick={() => toggleSpeak(currentStep)}
                  className={`mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-full font-serif font-semibold transition ${speaking ? "bg-amber-deep text-cream" : "bg-paper border border-line text-cocoa hover:border-amber-deep"}`}
                >
                  {speaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  {speaking ? "Detener" : "Escuchar con la voz de Papi"}
                </button>
                <p className="mt-2 text-xs text-cocoa-soft italic font-serif">
                  {voiceStatus.is_pvc
                    ? "Voz clonada de Papi (entrenada con sus grabaciones)."
                    : voiceStatus.ready
                    ? "Voz preliminar de Papi — la voz definitiva se está entrenando."
                    : "Voz temporal del iPhone — la voz de Papi se está preparando."}
                </p>
              </div>
            </motion.div>
          )}

          {showCapsule && capsule && (
            <motion.div key={`cap-${step}`} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.4 }} className="max-w-2xl mx-auto pt-4">
              <div className="bg-cream border border-amber-light rounded-lg overflow-hidden shadow-[0_20px_40px_-22px_rgba(168,93,38,0.45)]">
                {capsule.photo && (
                  <div className="relative w-full aspect-[4/3] bg-line">
                    <img src={capsule.photo} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-7">
                  <div className="flex items-center gap-2 mb-3">
                    <Heart className="w-4 h-4 text-amber-deep" />
                    <p className="eyebrow">Una pausa familiar</p>
                  </div>
                  <p className="font-display italic text-cocoa text-2xl mb-3">"{capsule.quote}"</p>
                  <p className="font-serif text-cocoa-soft text-base">{capsule.text}</p>
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <button
                      data-testid="speak-capsule-btn"
                      onClick={() => toggleSpeakCapsule(capsule)}
                      className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-serif font-semibold transition ${speaking ? "bg-amber-deep text-cream" : "bg-paper border border-line text-cocoa hover:border-amber-deep"}`}
                    >
                      {speaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                      {speaking ? "Detener" : "Escuchar a Papi"}
                    </button>
                    <button data-testid="continue-cooking-btn" onClick={() => { stopAudio(); setShowCapsule(false); }} className="btn-amber">
                      Sigamos cocinando <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {isFinished && (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-2xl mx-auto pt-12 text-center">
              <ChefHat className="w-16 h-16 text-amber-deep mx-auto mb-5" />
              <h1 className="font-display italic text-cocoa text-5xl mb-3">
                {displayName ? `¡Provecho, ${displayName}!` : (NICK_LABEL[cook] ? `¡Provecho, ${NICK_LABEL[cook]}!` : "¡Provecho, hijos!")}
              </h1>
              <p className="font-serif italic text-cocoa-soft text-xl mb-3">
                Lo cocinaste con tus manos. Eso es lo que importa.
              </p>
              <p className="font-serif text-cocoa text-base mb-8">
                Acabas de hacer <span className="italic text-amber-deep">{r?.nombre}</span>. Compártela con quien quieras.
              </p>
              <div className="flex flex-col gap-3 max-w-xs mx-auto">
                <button
                  data-testid="speak-farewell-btn"
                  onClick={toggleSpeakFarewell}
                  className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-serif font-semibold transition ${speaking ? "bg-amber-deep text-cream" : "bg-paper border border-line text-cocoa hover:border-amber-deep"}`}
                >
                  {speaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  {speaking ? "Detener" : "Escuchar a Papi"}
                </button>
                {cook && cook !== "invitado" && (
                  <button
                    data-testid="cook-upload-photo-btn"
                    onClick={() => setShowPhotoUpload(true)}
                    className="btn-amber"
                  >
                    <Camera className="w-4 h-4" /> Súbele foto a la familia
                  </button>
                )}
                <Link data-testid="back-to-recipe-btn" to={`/receta/${slug}`} className="btn-primary">Ver la receta</Link>
                <Link data-testid="back-home-btn" to="/cocina" className="btn-ghost">Volver al recetario</Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Bottom nav */}
      {!isFinished && (
        <div className="fixed bottom-0 left-0 right-0 bg-paper/95 backdrop-blur-md border-t border-line p-3 iphone-safe-bottom">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <button data-testid="prev-step-btn" onClick={handlePrev} disabled={step === 0} className="px-5 py-3 rounded-full bg-cream border border-line text-cocoa font-serif font-semibold disabled:opacity-30 inline-flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" /> Atrás
            </button>
            <button data-testid="next-step-btn" onClick={handleNext} disabled={false} className="btn-primary flex-1">
              {isIngredients ? "Listo, empecemos" : (step === total ? "Terminar" : "Siguiente paso")}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <PhotoUpload
        open={showPhotoUpload}
        onClose={() => setShowPhotoUpload(false)}
        member={cook}
        displayName={displayName || cook}
        recipeSlug={slug}
        recipeName={r?.nombre}
        onUploaded={() => toast.success("Foto guardada en la galería")}
      />
    </div>
  );
}
